import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Check, Copy, FileCode2, ChevronDown, ChevronRight } from 'lucide-react';
import 'katex/dist/katex.min.css';

/**
 * ChatMarkdown — Full-featured markdown renderer for AI chat.
 * Supports: GFM tables, syntax-highlighted code blocks with copy button,
 * LaTeX math, collapsible details, blockquotes, and more.
 */

/* ─── Copy button ─── */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs text-gray-400 hover:text-gray-200 hover:bg-white/10 transition-all"
    >
      {copied ? (
        <><Check className="w-3.5 h-3.5 text-green-400" /><span className="text-green-400">Copied!</span></>
      ) : (
        <><Copy className="w-3.5 h-3.5" /><span>Copy code</span></>
      )}
    </button>
  );
}

/* ─── Language labels ─── */
const LANG_LABELS = {
  js: 'JavaScript', javascript: 'JavaScript', jsx: 'React JSX',
  ts: 'TypeScript', typescript: 'TypeScript', tsx: 'React TSX',
  py: 'Python', python: 'Python',
  rb: 'Ruby', ruby: 'Ruby',
  go: 'Go', golang: 'Go',
  rs: 'Rust', rust: 'Rust',
  java: 'Java', kotlin: 'Kotlin', scala: 'Scala',
  cpp: 'C++', c: 'C', cs: 'C#', csharp: 'C#',
  sh: 'Shell', bash: 'Bash', zsh: 'Zsh', powershell: 'PowerShell', ps1: 'PowerShell',
  sql: 'SQL', graphql: 'GraphQL',
  html: 'HTML', css: 'CSS', scss: 'SCSS', less: 'LESS',
  json: 'JSON', yaml: 'YAML', yml: 'YAML', xml: 'XML', toml: 'TOML',
  tf: 'Terraform', hcl: 'HCL',
  md: 'Markdown', markdown: 'Markdown',
  docker: 'Dockerfile', dockerfile: 'Dockerfile',
  makefile: 'Makefile', cmake: 'CMake',
  swift: 'Swift', dart: 'Dart', lua: 'Lua', r: 'R', php: 'PHP',
  diff: 'Diff', plaintext: 'Plain Text', text: 'Text',
};

/* ─── Custom component renderers ─── */
const components = {
  code({ node, inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || '');
    const lang = match ? match[1] : '';
    const codeString = String(children).replace(/\n$/, '');

    // Block code
    if (!inline && (lang || codeString.includes('\n') || codeString.length > 80)) {
      const label = LANG_LABELS[lang] || (lang ? lang.charAt(0).toUpperCase() + lang.slice(1) : 'Code');
      return (
        <div className="my-4 rounded-xl overflow-hidden border border-gray-800 bg-[#1e1e2e] shadow-lg">
          <div className="flex items-center justify-between px-4 py-2.5 bg-[#181825] border-b border-gray-800">
            <div className="flex items-center gap-2">
              <FileCode2 className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs font-medium text-gray-400">{label}</span>
            </div>
            <CopyButton text={codeString} />
          </div>
          <SyntaxHighlighter
            style={oneDark}
            language={lang || 'text'}
            PreTag="div"
            showLineNumbers={codeString.split('\n').length > 3}
            lineNumberStyle={{ color: '#4a4a5a', fontSize: '0.75rem', paddingRight: '1em', minWidth: '2em' }}
            customStyle={{
              margin: 0,
              borderRadius: 0,
              fontSize: '0.85rem',
              lineHeight: '1.7',
              padding: '1rem 1.25rem',
              background: '#1e1e2e',
            }}
            {...props}
          >
            {codeString}
          </SyntaxHighlighter>
        </div>
      );
    }

    // Inline code
    return (
      <code className="px-1.5 py-0.5 rounded-md bg-gray-100 text-rose-600 text-[0.85em] font-mono font-medium" {...props}>
        {children}
      </code>
    );
  },

  /* Tables */
  table({ children }) {
    return (
      <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="min-w-full text-sm divide-y divide-gray-200">{children}</table>
      </div>
    );
  },
  thead({ children }) {
    return <thead className="bg-gray-50">{children}</thead>;
  },
  th({ children }) {
    return <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap">{children}</th>;
  },
  tr({ children }) {
    return <tr className="even:bg-gray-50/50 hover:bg-blue-50/30 transition-colors">{children}</tr>;
  },
  td({ children }) {
    return <td className="px-4 py-3 text-gray-700 border-t border-gray-100">{children}</td>;
  },

  /* Blockquotes */
  blockquote({ children }) {
    return (
      <blockquote className="my-4 pl-4 border-l-[3px] border-blue-500 bg-blue-50/60 py-3 pr-4 rounded-r-lg text-gray-700">
        {children}
      </blockquote>
    );
  },

  hr() {
    return <hr className="my-6 border-gray-200" />;
  },

  a({ href, children }) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline decoration-blue-400/50 underline-offset-2 transition-colors font-medium">
        {children}
      </a>
    );
  },

  h1({ children }) {
    return <h1 className="text-xl font-bold text-gray-900 mt-6 mb-3 pb-2 border-b border-gray-200">{children}</h1>;
  },
  h2({ children }) {
    return <h2 className="text-lg font-bold text-gray-900 mt-5 mb-2">{children}</h2>;
  },
  h3({ children }) {
    return <h3 className="font-semibold mt-4 mb-2" style={{ fontSize: '18px', color: '#1F1F1F' }}>{children}</h3>;
  },
  h4({ children }) {
    return <h4 className="text-sm font-semibold text-gray-800 mt-3 mb-1">{children}</h4>;
  },

  p({ children }) {
    return <p className="my-3" style={{ fontSize: '15px', lineHeight: '1.6', color: '#1F1F1F' }}>{children}</p>;
  },

  ul({ children }) {
    return <ul className="my-3 space-y-1.5 list-disc pl-6">{children}</ul>;
  },
  ol({ children }) {
    return <ol className="my-3 space-y-1.5 list-decimal pl-6">{children}</ol>;
  },
  li({ children }) {
    return (
      <li className="pl-1" style={{ fontSize: '15px', lineHeight: '1.6', color: '#1F1F1F' }}>
        {children}
      </li>
    );
  },

  img({ src, alt }) {
    return (
      <div className="my-4">
        <img src={src} alt={alt || ''} className="max-w-full rounded-xl border border-gray-200 shadow-md" loading="lazy" />
        {alt && <p className="text-xs text-gray-500 mt-2 text-center">{alt}</p>}
      </div>
    );
  },

  strong({ children }) {
    return <strong className="font-semibold text-gray-900">{children}</strong>;
  },

  em({ children }) {
    return <em className="italic">{children}</em>;
  },

  pre({ children }) {
    return <>{children}</>;
  },

  /* Collapsible details/summary */
  details({ children }) {
    return (
      <details className="my-3 border border-gray-200 rounded-lg overflow-hidden group">
        {children}
      </details>
    );
  },
  summary({ children }) {
    return (
      <summary className="px-4 py-2.5 bg-gray-50 hover:bg-gray-100 cursor-pointer text-sm font-medium text-gray-700 transition-colors flex items-center gap-2">
        <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
        {children}
      </summary>
    );
  },
};

/* ─── Main export ─── */
export default function ChatMarkdown({ content }) {
  if (!content) return null;

  return (
    <div className="chat-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
